import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminServices = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchServices();
  }, [navigate]);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/services`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateService = (index, field, value) => {
    const updated = [...services];
    updated[index] = { ...updated[index], [field]: value };
    setServices(updated);
  };

  const handleUpdateFeature = (serviceIndex, featureIndex, value) => {
    const updated = [...services];
    updated[serviceIndex].features[featureIndex] = value;
    setServices(updated);
  };

  const handleAddFeature = (serviceIndex) => {
    const updated = [...services];
    if (!updated[serviceIndex].features) {
      updated[serviceIndex].features = [];
    }
    updated[serviceIndex].features.push('');
    setServices(updated);
  };

  const handleRemoveFeature = (serviceIndex, featureIndex) => {
    const updated = [...services];
    updated[serviceIndex].features.splice(featureIndex, 1);
    setServices(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      for (const service of services) {
        await fetch(`${BACKEND_URL}/api/admin/services/${service.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(service)
        });
      }
      
      toast.success('Services updated successfully!');
    } catch (error) {
      toast.error('Failed to update services');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Services Management</h1>
              <p className="text-cyan-100">Edit service descriptions and features</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6">
        {services.map((service, index) => (
          <Card key={service.id}>
            <CardHeader>
              <CardTitle className="text-2xl">{service.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Service Title</Label>
                <Input
                  value={service.title}
                  onChange={(e) => handleUpdateService(index, 'title', e.target.value)}
                  placeholder="Service name"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={service.description}
                  onChange={(e) => handleUpdateService(index, 'description', e.target.value)}
                  placeholder="Service description"
                  rows={3}
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={service.image}
                  onChange={(e) => handleUpdateService(index, 'image', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Features</Label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleAddFeature(index)}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Feature
                  </Button>
                </div>
                <div className="space-y-2">
                  {service.features?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => handleUpdateFeature(index, featureIndex, e.target.value)}
                        placeholder="Feature description"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        onClick={() => handleRemoveFeature(index, featureIndex)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminServices;
